import { Controller } from "stimulus";
import { formatMoney, toMinorUnits } from "utils/money";

// The tip composer. Its element wraps the tip panel + the input form, so it owns the heart toggle,
// relabels the shared send button, and routes a submit to a tip (here) or a plain chat message
// (delegated to the sibling `chat-panel` controller). The Stripe.js flow runs in place; the pin
// arrives over the chat cable broadcast.
export default class ChatTip extends Controller {
  static targets = ["panel", "hint", "preset", "custom", "cardSection", "cardElement", "error", "sendButton", "sendIcon", "sendLabel", "heartButton"];

  static values = {
    contextType: String,
    contextId: Number,
    account: String,
    publicKey: String,
    currency: String,
    createUrl: String,
    minCents: Number,
    maxCents: Number,
    currentUserId: Number,
  };

  connect() {
    this.open = false;
    this.presetCents = null; // amount from a preset chip (custom takes precedence when typed)
    this.selectedCents = null; // effective amount, or null for a normal (non-tip) message
    this.clientTipId = null; // stable per charge attempt; rotated only on a definitive succeed/decline
    this.chargedCents = null; // amount last submitted under clientTipId; changing it rotates the key
    this.amountLocked = false; // set while an ambiguous/settling charge may have succeeded (see lockAmount)
    this.cardMounted = false;
    this.cardRevealed = false; // once true, submits collect a card via Elements instead of card-on-file
    this.inFlight = false;
    this.refresh();
  }

  disconnect() {
    if (this.cardEl) {
      this.cardEl.destroy();
      this.cardEl = null;
    }
    this.clearStripeWait();
  }

  // --- panel + amount selection ---------------------------------------------

  toggle() {
    this.open = !this.open;
    this.panelTarget.classList.toggle("hidden", !this.open);
    if (this.hasHeartButtonTarget) this.heartButtonTarget.classList.toggle("bg-red-400/20", this.open);
  }

  selectPreset(event) {
    if (this.amountLocked) return; // an ambiguous/settling charge is pending; amount is frozen
    const cents = parseInt(event.currentTarget.dataset.tipCents, 10);
    // Tapping the selected chip clears it (back to a normal message); typing custom wins over a chip.
    this.presetCents = this.presetCents === cents ? null : cents;
    this.customTarget.value = "";
    this.refresh();
  }

  customInput() {
    if (this.amountLocked) return;
    this.presetCents = null; // a typed custom amount clears any chip
    this.refresh();
  }

  effectiveCents() {
    const custom = this.parseCustomCents();
    if (custom != null) return custom;
    return this.presetCents;
  }

  parseCustomCents() {
    const raw = (this.customTarget.value || "").trim();
    if (raw === "") return null;
    const num = parseFloat(raw);
    if (isNaN(num) || num <= 0) return null;
    return toMinorUnits(num, this.currencyValue);
  }

  refresh() {
    const custom = this.parseCustomCents();
    const customActive = custom != null;
    this.selectedCents = this.effectiveCents();

    // Switching to a different amount rotates the key: replaying a burned idempotency key with a
    // new amount trips Stripe's idempotency_error. A same-amount retry keeps the id so it dedupes
    // against the pending charge. NEVER rotate while locked - that charge may have succeeded, so a
    // retry must reuse the key to dedupe rather than mint a second charge.
    if (!this.amountLocked && this.clientTipId != null && this.chargedCents != null &&
        this.selectedCents != null && this.selectedCents !== this.chargedCents) {
      this.rotateClientTipId();
    }

    this.presetTargets.forEach((chip) => {
      const cents = parseInt(chip.dataset.tipCents, 10);
      const active = !customActive && this.presetCents === cents;
      chip.classList.toggle("border-accent", active);
      chip.classList.toggle("bg-accent", active);
      chip.classList.toggle("text-white", active);
      chip.classList.toggle("border-gray-300", !active);
      chip.classList.toggle("bg-white", !active);
    });

    this.customTarget.classList.toggle("border-accent", customActive);
    this.customTarget.classList.toggle("bg-accent/10", customActive);
    this.customTarget.classList.toggle("border-gray-300", !customActive);
    this.customTarget.classList.toggle("bg-white", !customActive);

    this.updateSendButton();
    this.updateHint();
  }

  updateSendButton() {
    if (!this.hasSendButtonTarget) return;
    const tipping = this.selectedCents != null;

    if (this.hasSendIconTarget) this.sendIconTarget.classList.toggle("hidden", tipping);
    if (this.hasSendLabelTarget) {
      this.sendLabelTarget.classList.toggle("hidden", !tipping);
      if (tipping) this.sendLabelTarget.textContent = `Tip ${formatMoney(this.selectedCents, this.currencyValue)}`;
    }
    // Grow from the circular icon button into a labelled pill when a tip amount is active.
    this.sendButtonTarget.classList.toggle("w-10", !tipping);
    this.sendButtonTarget.classList.toggle("w-auto", tipping);
    this.sendButtonTarget.classList.toggle("px-4", tipping);
  }

  updateHint() {
    if (!this.hasHintTarget) return;
    this.hintTarget.textContent = this.selectedCents != null
      ? `${formatMoney(this.selectedCents, this.currencyValue)} · pins your message`
      : "pick any amount";
  }

  // --- submit routing -------------------------------------------------------

  submit(event) {
    event.preventDefault();
    if (this.inFlight) return;

    if (this.selectedCents == null) {
      // No amount selected: this is a normal chat message. Hand off to the chat-panel controller
      // (it owns the cable subscription + input clearing) so nothing about plain chat changes.
      const chatPanel = this.chatPanelController();
      if (chatPanel) chatPanel.send(event);
      return;
    }

    this.sendTip();
  }

  async sendTip() {
    const cents = this.selectedCents;
    if (cents < this.minCentsValue || cents > this.maxCentsValue) {
      this.showError(`Enter a tip between ${formatMoney(this.minCentsValue, this.currencyValue)} and ${formatMoney(this.maxCentsValue, this.currencyValue)}.`);
      return;
    }

    this.clearError();
    this.setInFlight(true);

    try {
      let paymentMethodId = null;
      if (this.cardRevealed) {
        paymentMethodId = await this.createPaymentMethod();
        if (!paymentMethodId) {
          this.setInFlight(false);
          return; // createPaymentMethod already surfaced the inline error
        }
      }

      const result = await this.postCreate(cents, paymentMethodId);
      await this.handleResult(result);
    } catch (error) {
      this.showError(this.stripeUnavailable(error)
        ? "Payments couldn't load. Disable any ad/script blocker and try again."
        : "Something went wrong sending your tip. Please try again.");
      this.setInFlight(false);
    }
  }

  async handleResult(result) {
    switch (result.status) {
      case "succeeded":
        this.onSucceeded();
        this.setInFlight(false);
        break;
      case "requires_action":
        await this.handle3DS(result);
        break;
      case "card_required":
        this.unlockAmount();
        await this.revealCard();
        this.showError(result.error || "Add a card to send a tip.");
        this.setInFlight(false);
        break;
      case "failed":
        // Definitive no-charge: a truly-succeeded original returns succeeded (Stripe idempotency),
        // never failed, so release any lock and rotate the burned key. Keep the message + amount.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError(result.error || "Your card was declined. Try a different card.");
        this.setInFlight(false);
        break;
      case "processing":
        // The charge was accepted and is settling; the webhook pins it once it clears. Lock the
        // amount (a new amount would mint a second charge) and don't rotate the key.
        this.lockAmount("Your tip is processing and will appear once it clears.");
        break;
      case "payment_error":
        // Ambiguous: the processor was unreachable, so the charge may have succeeded. Lock and keep
        // the key so a same-amount retry dedupes against the original instead of double-charging.
        this.lockAmount(result.error || "We couldn't reach the payment processor. Please try again.");
        break;
      default:
        // Other no-charge outcomes (invalid_amount / not_live / unavailable / forbidden): release
        // any lock, keep the message + amount, and surface a concise inline error. Never navigate.
        this.unlockAmount();
        this.showError(result.error || "We couldn't send that tip.");
        this.setInFlight(false);
    }
  }

  async handle3DS(result) {
    try {
      await this.ensureStripe();
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(result.client_secret);

      if (error) {
        // Auth failed - a definitive no-charge outcome, so release any ambiguous lock. The pending
        // intent needs a fresh attempt (new key), and a new card often helps.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError(error.message || "We couldn't verify your card. Try a different card.");
        this.setInFlight(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // The card cleared 3DS and was charged. From here the money is taken, so a confirm we
        // can't read must NOT surface as a hard failure - the webhook self-heals the pin.
        let confirmed;
        try {
          confirmed = await this.postConfirm(result.tip.id);
        } catch (error) {
          this.lockAmount("Your tip is processing and will appear shortly.");
          return;
        }

        if (confirmed.status === "succeeded") {
          this.onSucceeded();
          this.setInFlight(false);
        } else {
          // Charge already succeeded but confirm didn't return succeeded (processing / a transient
          // payment_error on retrieve): treat it as settling, not a failure. The webhook pins it.
          this.lockAmount("Your tip is processing and will appear shortly.");
        }
      } else if (paymentIntent && paymentIntent.status === "processing") {
        // Still settling: the payment_intent.succeeded webhook pins it once it clears. Lock the
        // amount (the same PI must settle, not be re-charged) rather than reveal the card.
        this.lockAmount("Your tip is processing and will appear once it clears.");
      } else {
        // Any other non-success (requires_payment_method / canceled) is a definitive no-charge:
        // release the lock, rotate, and let the tipper retry with a fresh card, like a decline.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError("Your card couldn't be charged. Try a different card.");
        this.setInFlight(false);
      }
    } catch (error) {
      this.showError(this.stripeUnavailable(error)
        ? "Payments couldn't load. Disable any ad/script blocker and try again."
        : "We couldn't complete the card verification. Please try again.");
      this.setInFlight(false);
    }
  }

  onSucceeded() {
    // Success is silent here: the pin + highlighted inline message arrive over the chat cable broadcast.
    const chatPanel = this.chatPanelController();
    if (chatPanel && chatPanel.hasInputTarget) chatPanel.clearInput();

    this.unlockAmount(); // a retry cleared the ambiguous/settling charge
    this.presetCents = null;
    this.customTarget.value = "";
    this.rotateClientTipId();
    this.clearError();
    this.hideCard();
    if (this.open) this.toggle();
    this.refresh();
    // Best-effort: after an async round trip, iOS may not reopen the keyboard.
    if (chatPanel) chatPanel.focusInput();
  }

  // The chat-panel controller forwards every tip pin as a `chat-panel:tip` window event. While we
  // hold an ambiguous/settling lock, our OWN pin arriving means the charge resolved, so unlock.
  // Filtered to the current user so another viewer's tip can't clear our lock prematurely.
  onTipBroadcast(event) {
    if (!this.amountLocked) return;

    const tip = event.detail;
    if (!tip || !tip.user) return;
    if (this.hasCurrentUserIdValue && String(tip.user.id) !== String(this.currentUserIdValue)) return;

    this.unlockAmount();
    this.clearError();
    this.refresh();
  }

  // --- network --------------------------------------------------------------

  postCreate(cents, paymentMethodId) {
    const chatPanel = this.chatPanelController();
    const body = chatPanel && chatPanel.hasInputTarget ? chatPanel.inputTarget.value.trim() : "";

    this.chargedCents = cents;
    return this.postJson(this.createUrlValue, {
      context_type: this.contextTypeValue,
      context_id: this.contextIdValue,
      amount_cents: cents,
      body: body,
      client_tip_id: this.ensureClientTipId(),
      payment_method_id: paymentMethodId,
    });
  }

  postConfirm(tipId) {
    return this.postJson(`${this.createUrlValue}/${tipId}/confirm`, {
      context_type: this.contextTypeValue,
      context_id: this.contextIdValue,
    });
  }

  // The endpoint returns structured JSON for every expected outcome, so parse regardless of HTTP
  // status. A non-JSON body (auth-redirect HTML / a 500 page) breaks that contract, so throw a
  // distinct error instead of an opaque response.json() failure.
  async postJson(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    });

    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) throw new Error("non_json_response");

    return response.json();
  }

  // --- Stripe.js ------------------------------------------------------------

  async ensureStripe() {
    if (this.stripe) return;
    await this.waitForStripe();
    // Scope Stripe to the creator's CONNECTED account so the card + PaymentMethod belong to it.
    this.stripe = window.Stripe(this.publicKeyValue, { stripeAccount: this.accountValue });
    this.elements = this.stripe.elements();
  }

  // Poll for Stripe.js, giving up after ~10s so a blocked script never leaves the shared send
  // button disabled forever. The interval id is held so disconnect() can clear a poll in flight.
  waitForStripe() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) return resolve();
      const startedAt = Date.now();
      this.stripeWaitInterval = setInterval(() => {
        if (window.Stripe) {
          this.clearStripeWait();
          resolve();
        } else if (Date.now() - startedAt > 10000) {
          this.clearStripeWait();
          reject(new Error("stripe_unavailable"));
        }
      }, 100);
    });
  }

  clearStripeWait() {
    if (this.stripeWaitInterval) {
      clearInterval(this.stripeWaitInterval);
      this.stripeWaitInterval = null;
    }
  }

  async ensureCardMounted() {
    await this.ensureStripe();
    if (this.cardMounted) return;

    this.cardEl = this.elements.create("card");
    this.cardEl.mount(this.cardElementTarget);
    this.cardEl.addEventListener("change", (e) => {
      if (e.error) this.showError(e.error.message);
      else if (e.complete) this.clearError();
    });
    this.cardMounted = true;
  }

  async revealCard() {
    await this.ensureCardMounted();
    this.cardSectionTarget.classList.remove("hidden");
    this.cardRevealed = true;
  }

  hideCard() {
    this.cardSectionTarget.classList.add("hidden");
    this.cardRevealed = false;
  }

  async createPaymentMethod() {
    await this.ensureCardMounted();
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({ type: "card", card: this.cardEl });
    if (error) {
      this.showError(error.message || "Please check your card details.");
      return null;
    }
    return paymentMethod.id;
  }

  // --- helpers --------------------------------------------------------------

  chatPanelController() {
    const el = this.element.closest('[data-controller~="chat-panel"]');
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, "chat-panel");
  }

  // True when the failure is the waitForStripe timeout (Stripe.js blocked / failed to load).
  stripeUnavailable(error) {
    return !!error && error.message === "stripe_unavailable";
  }

  // Freeze the amount and keep the current client_tip_id so a same-amount retry reuses the
  // idempotency key (dedupe against the original charge); a changed amount is blocked. Cleared by
  // onTipBroadcast when the pin arrives, or onSucceeded on retry.
  lockAmount(message) {
    this.amountLocked = true;
    this.setAmountEditingDisabled(true);
    this.showError(message);
    this.setInFlight(false);
  }

  unlockAmount() {
    this.amountLocked = false;
    this.setAmountEditingDisabled(false);
  }

  setAmountEditingDisabled(disabled) {
    this.presetTargets.forEach((chip) => { chip.disabled = disabled; });
    if (this.hasCustomTarget) this.customTarget.disabled = disabled;
  }

  ensureClientTipId() {
    if (!this.clientTipId) this.clientTipId = this.newUuid();
    return this.clientTipId;
  }

  rotateClientTipId() {
    this.clientTipId = null;
    this.chargedCents = null;
  }

  newUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    return `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  setInFlight(value) {
    this.inFlight = value;
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = value;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
    if (!this.open) this.toggle(); // make sure the panel (and its error) is visible
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.content : "";
  }
}
